import React, { DOMAttributes, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Tab, Tabs } from '@mui/material';
import { generatePath, Link, useNavigate, useParams } from 'react-router-dom';
import {
  BalanceChangeType,
  LeagueType,
  PreseasonAuctionBidStatus,
  PriceChangeType,
  ValueChangeType,
  WeekZeroType,
} from './generated/schemas';

export const SITE_TITLE = 'College Pick Em 2026';

function getOptions(labelObject: Record<string, string>) {
  return Object.entries(labelObject).map(([value, label]) => ({ label, value }));
}

export const leagueTypeLabel: Record<LeagueType, string> = {
  PICK_EM: "Pick 'Em",
  STOCK_MARKET: 'Stock Market',
  STOCK_MARKET_SIMULATION: 'Stock Market (Simulation)',
};

export const leagueTypeOptions = getOptions(leagueTypeLabel);

export const creatableLeagueTypeOptions = leagueTypeOptions.filter(
  option => option.value !== LeagueType.StockMarketSimulation,
);

export const weekZeroTypeLabel: Record<WeekZeroType, string> = {
  REGULAR: 'Regular',
  PRACTICE: 'Practice',
  IGNORE: 'Ignore',
};

export const weekZeroTypeOptions = getOptions(weekZeroTypeLabel);

export const balanceChangeTypeLabel: Record<BalanceChangeType, string> = {
  INITIAL_BALANCE: 'Initial Balance',
  SHARES_PURCHASED: 'Shares Purchased',
  SHARES_SOLD: 'Shares Sold',
  SHARES_SHORTED: 'Shares Shorted',
  SHARES_COVERED: 'Shares Covered',
  DIVIDENDS_RECEIVED: 'Dividends Received',
  SHORT_DIVIDENDS_RECEIVED: 'Short Dividends',
  POSTSEASON_DIVIDENDS_RECEIVED: 'Postseason Dividends',
  POSTSEASON_DIVIDENDS_PAID: 'Postseason Dividends Charged',
};

export const valueChangeTypeLabel: Record<ValueChangeType, string> = {
  INITIAL_VALUE: 'Initial Value',
  INITIAL_PURCHASES: 'Initial Purchases',
  GAME_WON: 'Owned Team Won Game',
  NEW_RATINGS: 'New Team Ratings',
  SHARES_PURCHASED: 'Shares Purchased',
  SHARES_SOLD: 'Shares Sold',
  SHARES_SHORTED: 'Shares Shorted',
  SHARES_COVERED: 'Shares Covered',
  OTHER_USER_SHARES_PURCHASED: 'Team Price Changed',
  OTHER_USER_SHARES_SOLD: 'Team Price Changed',
  OTHER_USER_SHARES_SHORTED: 'Team Price Changed',
  OTHER_USER_SHARES_COVERED: 'Team Price Changed',
  DIVIDENDS_RECEIVED: 'Dividends Received',
  SHORT_DIVIDENDS_RECEIVED: 'Short Dividends',
  POSTSEASON_DIVIDENDS_RECEIVED: 'Postseason Dividends',
  POSTSEASON_DIVIDENDS_PAID: 'Postseason Dividends Charged',
};

export const preseasonAuctionBidStatusLabel: Record<PreseasonAuctionBidStatus, string> = {
  PENDING: 'Pending',
  ALLOCATED: 'Won',
  OUTBID: 'Outbid',
  OUTQUEUED: 'Lost Tie-Break',
  INSUFFICIENT: 'Insufficient Funds',
  CANCELED: 'Canceled',
};

export const priceChangeTypeLabel: Record<PriceChangeType, string> = {
  INITIAL_PRICE: 'Initial Price',
  INITIAL_PURCHASES: 'Initial Purchases',
  GAME_WON: 'Game Won',
  SHARES_PURCHASED: 'Shares Purchased',
  SHARES_SOLD: 'Shares Sold',
  SHARES_SHORTED: 'Shares Shorted',
  SHARES_COVERED: 'Shares Covered',
  NEW_RATINGS: 'New Ratings',
};

export function buildUrl(url: string, host: string = process.env.REACT_APP_API_URL) {
  return `${host}${url}`;
}

type HoverProps = Pick<DOMAttributes<Element>, 'onMouseEnter' | 'onMouseLeave'>;
type UseHoverResult = [HoverProps, boolean];

export function useHover() {
  const [hover, setHover] = useState(false);
  const onMouseEnter = useCallback(() => setHover(true), [setHover]);
  const onMouseLeave = useCallback(() => setHover(false), [setHover]);
  const hoverProps: HoverProps = useMemo(() => ({ onMouseEnter, onMouseLeave }), [onMouseEnter, onMouseLeave]);
  const result: UseHoverResult = useMemo(() => [hoverProps, hover], [hoverProps, hover]);
  return result;
}

type TabConfig<P = Record<string, unknown>> = {
  route: string;
  label: React.ReactNode;
  Component: React.ComponentType<P>;
};

export const fullWidthDividerSx = {
  mx: { xs: -1, sm: -2 },
  px: { xs: 1, sm: 2 },
};

export const APP_HEADER_HEIGHT_XS = 56;
export const APP_HEADER_HEIGHT_SM = 64;

// Rendered height of a useTabs Tabs bar (48px MUI default + 1px borderBottom). A second, nested
// sticky tab bar needs to stick below the first one rather than under it -- pass this as
// `stickyOffset` in that case. Doesn't vary by breakpoint, unlike the header height above.
export const TABS_BAR_HEIGHT = 49;

// Returns the tabs UI already rendered (`tabs: ReactNode`), NOT a component to instantiate via
// `<TabsComponent />` (an earlier version of this hook did exactly that, via useCallback). That pattern
// is a footgun: a new function reference from useCallback -- which happens on every dependency change,
// including ones as mundane as a live badge count ticking -- is a NEW COMPONENT TYPE as far as React's
// reconciler is concerned. Rendering `<newFunctionReference />` where `<oldFunctionReference />` used to
// be doesn't just re-render, it unmounts the entire previous tree (every tab's content, not just the
// active one) and mounts a fresh one, silently discarding all of it -- e.g. this is what caused Chat's
// own initial anchor/pagination fetches to unnecessarily re-run every time a mark-read call ticked
// chatUnreadCount, since that count feeds a tabConfig useMemo elsewhere that ultimately fed this hook.
// Returning a plain ReactNode sidesteps the whole class of bug: React reconciles the returned tree by
// each element's own type and position, not by any wrapping function's identity, so the active tab's
// content only ever re-renders (preserving its state) when this hook is called again, never remounts.
type UseTabsParams<P> = {
  tabConfig: TabConfig<P>[];
  path: string;
  pathSegment: string;
  componentProps?: P;
  activeRoute?: string;
  sticky?: boolean;
  // Extra px added on top of the header height when sticky, for a tab bar stacking below another
  // sticky element (e.g. a nested useTabs bar below the page's own sticky tab bar).
  stickyOffset?: number;
};

export function useTabs<P = Record<string, unknown>>({
  tabConfig,
  path,
  pathSegment,
  componentProps = {} as P,
  activeRoute,
  sticky = false,
  stickyOffset = 0,
}: UseTabsParams<P>) {
  const { [pathSegment]: pathSegmentValue } = useParams();
  const selectedRoute = activeRoute ?? pathSegmentValue;
  const navigate = useNavigate();

  const tab = tabConfig.findIndex(config => config.route === selectedRoute);

  useEffect(() => {
    if (tab === -1 && activeRoute === undefined) {
      navigate(generatePath(path, { [pathSegment]: tabConfig[0].route }), { replace: true });
    }
  }, [tab, activeRoute]);

  // Switching tabs opens the new tab wherever the previous one happened to be scrolled -- reset to the
  // top on each change. Skips the first render (deep link / initial mount) and any URL carrying a hash,
  // so an in-page anchor target (e.g. the rules pages' #section links) still wins.
  const scrolledForRoute = useRef(selectedRoute);
  useEffect(() => {
    if (selectedRoute === scrolledForRoute.current) {
      return;
    }
    scrolledForRoute.current = selectedRoute;
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
  }, [selectedRoute]);

  const Component = tabConfig[tab]?.Component;
  const tabs = (
    <>
      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        value={tab === -1 ? false : tab}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTabScrollButton-root': { width: 'unset' },
          '& .MuiTabScrollButton-root:first-child': { ml: -1 },
          '& .MuiTabScrollButton-root:last-child': { mr: -1 },
          ...fullWidthDividerSx,
          ...(sticky && {
            position: 'sticky',
            top: { xs: APP_HEADER_HEIGHT_XS + stickyOffset, sm: APP_HEADER_HEIGHT_SM + stickyOffset },
            zIndex: theme => theme.zIndex.appBar - 1,
            bgcolor: 'background.paper',
          }),
        }}
      >
        {tabConfig.map(config => (
          <Tab
            key={config.route}
            label={config.label}
            component={Link}
            to={generatePath(path, { [pathSegment]: config.route })}
          />
        ))}
      </Tabs>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        {Component && (
          <Suspense
            fallback={
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <CircularProgress />
              </Box>
            }
          >
            <Component {...componentProps} />
          </Suspense>
        )}
      </Box>
    </>
  );

  return { tabs };
}
